import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import * as React from 'react'
import { usePageActivityTracking } from 'analytics/snowplow/helpers/useActivityTracking'
import { type AdditionalData, type OfferListEventsDispatchAction } from 'components/OfferList/OfferListEventsContext'
import { connect } from 'react-redux'
import TrendingDestinations from '../../components/TrendingDestinations/TrendingDestinations'
import Search from '../../components/Search/Search'
import Pane from 'components/Common/Pane'
import { isLEStaff, selectLoggedIn } from 'selectors/accountSelectors'
import HeroOfferCarouselSection from '../../components/OfferCarousels/HeroOfferCarouselSection'
import HotelOfferCarousel from '../../components/OfferCarousels/HotelOfferCarousel'
import TourOfferCarousel from '../../components/OfferCarousels/TourOfferCarousel'
import ValuePropositionBanner from '../../components/ValuePropositionBanner/ValuePropositionBanner'
import MarketingCarousel from '../../components/MarketingCarousel/MarketingCarousel'
import BlogCarousel from '../../components/Blog/BlogCarousel'
import BrowseOfferTypes from 'home/components/BrowseOfferTypes/BrowseOfferTypes'
import SmartExperienceOfferCarousel from 'home/components/OfferCarousels/ExperienceOfferCarousel/SmartExperienceOfferCarousel'
import GeoContext from 'contexts/geoContext'
import UltraLuxOfferCarousel from 'home/components/OfferCarousels/UltraLuxHotelOfferCarousel'
import config from 'constants/config'
import TopPicksForMeCarousel from 'home/components/OfferCarousels/TopPicksForMeCarousel'
import OnlyInAustraliaHeroCarousel from 'home/components/OfferCarousels/OnlyInAustraliaHeroCarousel'
import MaldivesCharterCarousel from 'home/components/OfferCarousels/MaldivesCharterCarousel'
import OfferEndingSoonCarousel from 'home/components/OfferCarousels/OfferEndingSoonCarousel'
import PromptCardSelector from 'components/Common/StickyPromptCard/PromptCardSelector'
import LeLivePrompt from 'leLive/components/LeLivePrompt'
import { OfferListEventsProvider, OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import useHomepageAnalytics from 'home/pages/HomePage/useHomepageAnalytics'
import { trackDurationMs } from 'analytics/perf'
import { rem } from 'polished'
import styled from 'styled-components'
import { GlobalSearchTrackingProvider } from 'contexts/GlobalSearch/GlobalSearchTracking'
import HomepageFlightDeals from 'home/components/FlightDeals/HomepageFlightDeals'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import ErrorBoundary from 'components/Common/ErrorBoundary/ErrorBoundary'
import TrustIndicatorSection from 'home/components/TrustIndicatorSection/TrustIndicatorSection'
import { isStandaloneCruiseEnabled } from 'selectors/cruiseOfferSelectors'
import FrontPageUSPComponent from 'components/Pages/FrontPage/USP/FrontPageUSPComponent'
import Divider from 'LuxKit/Divider'
import LayoutContainer from 'components/Common/LayoutContainer'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import SupportTile from 'components/Common/SupportTile/SupportTile'
import { InView } from 'react-intersection-observer'
import { NO_24_7_SUPPORT_REGIONS } from 'constants/config/region'
import useQueryParams from 'hooks/useQueryParams'
import LuxPlusOfferCarousel from 'home/components/OfferCarousels/LuxPlusOfferCarousel'
import SocieteStayUpgradeCarousel from 'home/components/OfferCarousels/SocieteStayUpgradeCarousel'
import useShouldShowSocieteStayUpgradeCarousel from 'home/hooks/useShouldShowSocieteStayUpgradeCarousel'
import { getCanRedeemLuxPlusBenefits, getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import MerchandisePointsCarousel from 'components/Merchandise/MerchandiseCarousels/MerchandisePointsCarousel'
import { getIsMerchandiseEnabled } from 'selectors/merchandiseSelectors'
import { isEnglishLocale } from 'i18n/util/locale'
import RequestAgentCallbackModal from 'supportAssistant/components/RequestAgentCallback/Modals/RequestAgentCallbackModal'
import ModalContext from 'contexts/ModalContext'
import { removeSearchParamValue } from 'lib/url/searchUrlUtils'
import { replace } from 'lib/routing/appRouting'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { useLocation } from 'react-router-dom'
import CompactBookingDetailsUpsellSection from 'checkout/Components/Confirmation/BookingDetailsV2/CompactBookingDetailsUpsellSection'
import { fetchOrders } from 'actions/OrderActions'
import { haveOrdersBeenFetched, sortOrdersByDeparture } from 'lib/order/orderUtils'
import { getUpcomingOrders } from 'selectors/orderSelectors'
import { upsellEnabledForOrder } from 'lib/order/upsellUtils'
import OfferDetailUpsellSection from 'checkout/Components/Confirmation/BookingDetailsV2/OfferDetailUpsellSection'
import Group from 'components/utils/Group'
import {
  REQUEST_FLIGHT_SUPPORT_CALLBACK_PATH,
  REQUEST_SALES_CALLBACK_PATH,
  REQUEST_SUPPORT_CALLBACK_PATH,
  REQUEST_TOURS_CALLBACK_PATH,
} from 'supportAssistant/components/RequestAgentCallback/RequestAgentCallbackUtils'
import RecentSearches from 'home/components/RecentSearches/RecentSearches'
import PostTripReviewNudge from 'home/components/PostTripReviewNudge/PostTripReviewNudge'
import { MALDIVES_HUNDRED_DOLLAR_SALE_MODAL_SESSION_KEY, TERMS_AND_CONDITIONS_ACKNOWLEDGED_PATH } from 'home/constants'
import TermsAndConditionsAcknowledgedModal from 'components/App/TermsAndConditionsModal/TermsAndConditionsAcknowledgedModal'
import MaldivesDirectHundredDollarSaleModal from 'components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesDirectHundredDollarSaleModal'
import { LUXURY_ESCAPES } from 'constants/brands'
import { ChertoExperiments, Experiments } from 'constants/experiments'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import useFeatureFlag from 'hooks/Experiments/useFeatureFlag'
import useExperiment from 'hooks/Experiments/useExperiment'
import useCharterFlightPromotionAvailableSeats from 'hooks/Flights/useCharterFlightPromotionAvailableSeats'
import useOfferList from 'hooks/Offers/useOfferList'
import { interleaveWithRatio, skip, take, unique, without } from 'lib/array/arrayUtils'
import { getHomeOfferListParams, getToursOfferListFilters, hasRecentTourViews } from 'home/helpers'
import useOrderItemsEligibleForCarHireBundle from 'hooks/CarHire/useOrderItemsEligibleForCarHireBundle'
import CarHireBundledUpcomingOrderCondensed from 'components/CarHire/CarHireBundledUpcomingOrders/CarHireBundledUpcomingOrderCondensed'
import { HomepageFlightCampaigns } from 'home/components/FlightCampaigns/HomepageFlightCampaigns'
import { useShowFlightCampaignsOrDeals } from 'hooks/Flights/useCampaignsOrDeals'
import { isCarHireBundledHomepageCarouselEnabled } from 'selectors/carHireSelectors'
import { isMaldivesCharterCarouselEnabled } from 'selectors/maldivesSelectors'
import Leaderboard from 'components/Common/Banner/Leaderboard'
import { fetchBanners } from 'actions/ContentActions'
import { BANNER_LOCATION_HOME } from 'constants/bannerLocations'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { selectTwoTowerOfferListFiltersByUser } from 'selectors/recommendations'
import CruiseOfferCarousel from 'home/components/OfferCarousels/CruiseOfferCarousel'
import CruiseRoyalCaribbeanCampaignCarousel from 'home/components/OfferCarousels/CruiseRoyalCaribbeanCampaignCarousel'
import ExperienceLuxPlusCarousel from 'components/Experiences/ExperienceLists/ExperienceLuxPlusCarousel'
import ComponentBannerGroup from 'marketing/components/ComponentBannerGroup'
import MyVouchersCarousel from 'marketing/components/MyVouchersCarousel'
import { useUserInteractions } from 'hooks/Recommendation/useUserInteractions'
import HomepageCarouselContext from 'contexts/OfferList/HomepageCarouselContext'
import { isAgentHubEnabled } from 'agentHub/selectors/agentHubSelectors'
import { useCurrentSiteTakeover } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import DoublePointTakeoverSubtitleContent from 'takeovers/societeDoublePointsTakeover/components/DoublePointTakeoverSubtitleContent'
import DoublePointTakeoverTitleContent from 'takeovers/societeDoublePointsTakeover/components/DoublePointTakeoverTitleContent'
import { SOCIETE_DOUBLE_POINTS_TAKEOVER_ID } from 'takeovers/societeDoublePointsTakeover/constants/societeDoublePointsTakeoverConstants'
import TriplePointTakeoverCampaignSubtitle from 'takeovers/societyTriplePointsTakeover/components/TriplePointTakeoverCampaignSubtitle'
import TriplePointTakeoverHeroTitle from 'takeovers/societyTriplePointsTakeover/components/TriplePointTakeoverHeroTitle'
import { triplePointsRoute, triplePointsTakeoverId } from 'takeovers/societyTriplePointsTakeover/societyTriplePointsTakeoverConfig'
import TasteOfGoldHeroSubtitleContent from 'takeovers/tasteOfGoldTakeover/components/TasteOfGoldHeroSubtitleContent'
import TasteOfGoldHeroLogo from 'takeovers/tasteOfGoldTakeover/components/TasteOfGoldHeroLogo'
import GoldText from 'takeovers/tasteOfGoldTakeover/components/GoldText'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import getTasteOfGoldPromoCampaign from 'takeovers/tasteOfGoldTakeover/selectors/getTasteOfGoldPromoCampaign'
import { isManualSortingEnabledFor } from 'selectors/featuresSelectors'
import { ALTERNATIVES_RECENTLY_VIEWED_LIMIT } from 'components/Recommendations/common/constant'
import { useLabFeatureFlag } from 'hooks/FeatureFlag/useLabFeatureFlag'
import { LabFeatureFlags } from 'constants/lab'
import { isLabCarouselSectionOrderPayload } from 'lib/lab/dynamicPayload'
import { DEFAULT_CAROUSEL_ORDER } from './constants'
import { FormattedMessage, useIntl } from 'react-intl'
import useTasteOfGoldHashModal from 'takeovers/tasteOfGoldTakeover/hooks/useTasteOfGoldHashModal'

const EMPTY_BANNERS: Array<App.Banner> = []
const LeaderboardLayoutContainer = styled(Pane)`
  padding-top: ${rem(12)};
  position: relative;

  ${mediaQueryUp.tablet} {
    padding-top: ${rem(40)};
  }
`

const HomepageSection = styled(LayoutContainer)`
  padding-top: ${rem(40)};
`

const PaddedLayoutContainer = styled(LayoutContainer)`
  padding-top: ${rem(40)};
  padding-bottom: ${rem(40)};
`

const CarHireCarouselSection = styled.div`
  padding-top: ${rem(40)};
`

const FlightDealsContainer = styled.div`
  padding-top: ${rem(40)};
  padding-bottom: ${rem(40)};
`

interface MappedProps {
  banners?: Array<App.Banner>
  isLoggedIn: boolean
  isStandaloneCruiseEnabled: boolean
  isLuxPlusEnabled: boolean
  canRedeemLuxPlusBenefits: boolean
  carHireBundledCarouselEnabled: boolean
  maldivesCharterCarouselEnabled: boolean
}

function HomePage(props: MappedProps) {
  const intl = useIntl()
  usePageActivityTracking()
  useTasteOfGoldHashModal()

  const heroCarouselStartRef = useRef<number>(0)
  const heroCarouselSentRef = useRef<boolean>(false)
  useEffect(() => {
    heroCarouselStartRef.current = performance.now()
    heroCarouselSentRef.current = false
  }, [])
  const {
    isLoggedIn,
    isStandaloneCruiseEnabled,
    isLuxPlusEnabled,
    canRedeemLuxPlusBenefits,
    carHireBundledCarouselEnabled,
    maldivesCharterCarouselEnabled,
    banners,
  } = props
  const query = useQueryParams()
  const showFlightSupportCallbackModal = query.has(REQUEST_FLIGHT_SUPPORT_CALLBACK_PATH)
  const showSupportCallbacksModal = query.has(REQUEST_SUPPORT_CALLBACK_PATH)
  const showSalesCallbacksModal = query.has(REQUEST_SALES_CALLBACK_PATH)
  const showToursCallbacksModal = query.has(REQUEST_TOURS_CALLBACK_PATH)
  const { currentRegionCode, geoStateName } = useContext(GeoContext)
  const ordersFetched = useAppSelector((state) => haveOrdersBeenFetched(state, 'upcoming'))
  const upcomingOrders = useAppSelector((state) => getUpcomingOrders(state))
  const campaignsOrDeals = useShowFlightCampaignsOrDeals(true)
  const isOnlyInAustraliaHeroCarouselEnabled = useFeatureFlag(OptimizelyFeatureFlags.croOnlyInAustraliaCarousel)
  const isCharterLaunchOffer100FeatureEnabled = useFeatureFlag(OptimizelyFeatureFlags.charterLaunchOffer100)
  const isStaff = useAppSelector(isLEStaff)
  const isCharterLaunchOffer100Enabled =
    isCharterLaunchOffer100FeatureEnabled && !isAgentHubEnabled && !isStaff
  const labCarouselSectionOrder = useLabFeatureFlag(LabFeatureFlags.enableHomePageCarouselReorder)
  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)
  const isPostLoginExperimentSyncing = useAppSelector(state => state.optimizely.isPostLoginExperimentSyncing)
  const shouldShowPostTripReviewNudge = config.BRAND === LUXURY_ESCAPES && isLoggedIn

  const {
    availableSeats: charterPromotionAvailableSeats,
    isLoading: isLoadingCharterPromotionAvailableSeats,
  } = useCharterFlightPromotionAvailableSeats({
    enabled: isCharterLaunchOffer100Enabled,
  })

  const carouselSectionOrder = useMemo(() => {
    // If flag is enabled and payload is valid, use lab payload
    if (labCarouselSectionOrder?.enabled) {
      const payload = labCarouselSectionOrder?.value
      if (isLabCarouselSectionOrderPayload(payload)) {
        return payload.sectionOrder
      }
    }
    // Otherwise, use default order
    return DEFAULT_CAROUSEL_ORDER
  }, [labCarouselSectionOrder])

  const showSupportContainer = !NO_24_7_SUPPORT_REGIONS.includes(currentRegionCode)
  const upsellTilesFeatureToggle = useFeatureFlag(OptimizelyFeatureFlags.upsellTilesFeatureToggle)
  useExperiment(Experiments.croHomepageHeroOffersSsrTimeout)
  useExperiment(ChertoExperiments.checkoutCopy)
  useExperiment(ChertoExperiments.homepageAa)
  const showFrontPageUSP = !isLoggedIn
  const { isAnyVariant: hideHomepageSearch } = useExperiment(Experiments.croHideHomepageSearchEmergingMarkets)

  const termsAndConditionsAcknowledgedCartId = query.get(TERMS_AND_CONDITIONS_ACKNOWLEDGED_PATH)

  const { search } = useLocation()
  const showModal = useContext(ModalContext)
  const dispatch = useAppDispatch()

  const handleRequestAgentCallbackModal = useCallback((callbackType: App.GenesysAgentCallbackType, callbackPath: string) => {
    showModal(<RequestAgentCallbackModal type={callbackType} />)

    const nextSearch = removeSearchParamValue(search, callbackPath, 'true')
    dispatch(replace({ search: nextSearch }))
  }, [dispatch, search, showModal])

  useEffect(() => {
    if (showSalesCallbacksModal) {
      handleRequestAgentCallbackModal('SALES', REQUEST_SALES_CALLBACK_PATH)
    } else if (showSupportCallbacksModal) {
      handleRequestAgentCallbackModal('SUPPORT', REQUEST_SUPPORT_CALLBACK_PATH)
    } else if (showToursCallbacksModal) {
      handleRequestAgentCallbackModal('TOURS', REQUEST_TOURS_CALLBACK_PATH)
    } else if (showFlightSupportCallbackModal) {
      handleRequestAgentCallbackModal('FLIGHTS', REQUEST_FLIGHT_SUPPORT_CALLBACK_PATH)
    }
  }, [
    dispatch,
    search,
    showFlightSupportCallbackModal,
    showSupportCallbacksModal,
    showSalesCallbacksModal,
    showToursCallbacksModal,
    showModal,
    handleRequestAgentCallbackModal,
  ])

  useEffect(() => {
    if (
      !isCharterLaunchOffer100Enabled ||
      isLoadingCharterPromotionAvailableSeats ||
      charterPromotionAvailableSeats <= 0 ||
      sessionStorage.getItem(MALDIVES_HUNDRED_DOLLAR_SALE_MODAL_SESSION_KEY)
    ) {
      return
    }

    sessionStorage.setItem(MALDIVES_HUNDRED_DOLLAR_SALE_MODAL_SESSION_KEY, 'true')
    showModal(<MaldivesDirectHundredDollarSaleModal />)
  }, [
    charterPromotionAvailableSeats, isCharterLaunchOffer100Enabled, isLoadingCharterPromotionAvailableSeats, showModal])

  useEffect(() => {
    if (termsAndConditionsAcknowledgedCartId) {
      showModal(<TermsAndConditionsAcknowledgedModal cartId={termsAndConditionsAcknowledgedCartId} />)

      const nextSearch = removeSearchParamValue(search, TERMS_AND_CONDITIONS_ACKNOWLEDGED_PATH, 'true')
      dispatch(replace({ search: nextSearch }))
    }
  }, [termsAndConditionsAcknowledgedCartId, showModal, dispatch, search])

  useEffect(() => {
    if (!ordersFetched) {
      dispatch(fetchOrders({ status: 'upcoming' }))
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  useEffect(() => {
    dispatch(fetchBanners(BANNER_LOCATION_HOME))
  }, [dispatch])
  const bannersAboveHero = useMemo(() =>
    banners?.filter((banner) => banner.position === 'homepage-above-hero-carousel'),
  [banners],
  )

  const bannersBelowHero = useMemo(() =>
    banners?.filter((banner) => banner.position === 'homepage-below-search-bar'),
  [banners],
  )

  const upsellOrder = useMemo(() => {
    return sortOrdersByDeparture(upcomingOrders)
      .find(upsellEnabledForOrder)
  }, [upcomingOrders])

  const showUpsellSection = ordersFetched &&
    upsellTilesFeatureToggle &&
    !!upsellOrder

  const {
    onTourListEvent,
    onCruiseListEvent,
    onHotelListEvent,
    onBestValueHotelListEvent,
    onUltraLuxListEvent,
    onHeroListEvent,
    onSearchEvent,
    onTrendingDestinationsListEvent,
    onExperienceListEvent,
    onTPFMHeroListEvent,
    onTPFMNonHeroListEvent,
    onCuratedNonHeroListEvent,
    onFlightListEvent,
    onLuxPlusListEvent,
    onEndingSoonListEvent,
  } = useHomepageAnalytics()

  const handleHeroListEvent = useCallback((dispatchAction: OfferListEventsDispatchAction) => {
    onHeroListEvent(dispatchAction)
    if (!heroCarouselSentRef.current && dispatchAction.type === OfferListEvents.offerReady) {
      heroCarouselSentRef.current = true
      trackDurationMs('homepage_hero_carousel_load_time_ms', heroCarouselStartRef.current)
    }
  }, [onHeroListEvent])

  const handleTPFMHeroListEvent = useCallback((dispatchAction: OfferListEventsDispatchAction, additionalData?: AdditionalData) => {
    onTPFMHeroListEvent(dispatchAction, additionalData)
    if (!heroCarouselSentRef.current && dispatchAction.type === OfferListEvents.offerReady) {
      heroCarouselSentRef.current = true
      trackDurationMs('homepage_hero_carousel_load_time_ms', heroCarouselStartRef.current)
    }
  }, [onTPFMHeroListEvent])

  const { earliestItem } = useOrderItemsEligibleForCarHireBundle(upsellOrder?.id)
  const showBundledCarHireCarousel = showUpsellSection && carHireBundledCarouselEnabled && !!earliestItem && !!upsellOrder?.hasCarHireIntention
  const showSocieteStayUpgradeCarousel = useShouldShowSocieteStayUpgradeCarousel()

  /** START: Lere Hero Carousel */
  const heroOffers = useOfferList({
    ...getHomeOfferListParams({
      region: currentRegionCode,
      subregion: geoStateName,
    }),
  })
  const lereHeroCarouselEnabled: boolean = useFeatureFlag(OptimizelyFeatureFlags.lereHeroCarouselEnabled)
  const twoTowersOfferListFilters = useAppSelector(selectTwoTowerOfferListFiltersByUser)
  const twoTowersOfferList = useOfferList(twoTowersOfferListFilters, { disabled: !lereHeroCarouselEnabled })
  const [userPageViewsLast7DaysInteractions, userPageViewsLast7DaysFetching] = useUserInteractions({ interactionType: 'view', sinceDaysAgo: 7, disabled: !lereHeroCarouselEnabled })

  const isToursManualSortingEnabled = isManualSortingEnabledFor('tours')
  const toursOfferListFilters = useMemo(() => getToursOfferListFilters(isToursManualSortingEnabled), [isToursManualSortingEnabled])
  const toursOfferList = useOfferList(toursOfferListFilters)
  const highIntentOffers = useAppSelector(state => state.recommendations.highIntent)

  const interleaveOfferListWithTours = useCallback((offerList: App.OfferList, firstCuratedOffer?: string): [App.OfferList, Array<string>] => {
    const fetching = highIntentOffers.state === 'loading' || offerList.fetching
    const hasTourView = hasRecentTourViews(
      take(highIntentOffers.offers, ALTERNATIVES_RECENTLY_VIEWED_LIMIT),
    )

    // Skip interleaving if no offers or no recent tour view in the last 7 offer views
    if (!offerList.offerIds.length || !hasTourView) {
      return [{ ...offerList, fetching }, []]
    }

    // Remove the first curated offer before interleaving to preserve correct hotel/tour pattern
    const offerListWithoutHero = firstCuratedOffer ? without(offerList.offerIds, firstCuratedOffer) : offerList.offerIds

    // Guard against empty list after removing hero - skip interleaving if no offers remain
    if (offerListWithoutHero.length === 0) {
      return [{ ...offerList, fetching }, []]
    }

    const toursToInterleave: Array<string> = take(toursOfferList.offerIds, 3)
    const offerListOfferIdsSkipFirst = skip(offerListWithoutHero, 1)

    return [
      {
        ...offerList,
        fetching,
        // hotel, tour, hotel, hotel, tour, ...
        offerIds: [offerListWithoutHero[0], ...interleaveWithRatio(toursToInterleave, offerListOfferIdsSkipFirst, { antecedent: 1, consequent: 2 })],
      },
      toursToInterleave,
    ]
  }, [highIntentOffers, toursOfferList])

  const firstCuratedOffer = heroOffers.offerIds[0]
  const [interleavedTwoTowersOfferList, excludedTourIds] = interleaveOfferListWithTours(twoTowersOfferList, firstCuratedOffer)

  const canActivateTwoTowersNonAUHeroCarousel = lereHeroCarouselEnabled &&
    interleavedTwoTowersOfferList.offerIds.length >= 9 &&
    !!userPageViewsLast7DaysInteractions.length &&
    currentRegionCode !== 'AU'
  const { isAnyVariant: isTwoTowersNonAUHeroCarouselVariant } = useExperiment(
    Experiments.lereNonAUTwoTowersHomePageHeroCarouselV2,
    { canActivate: canActivateTwoTowersNonAUHeroCarousel },
  )
  const twoTowersNonAUHeroCarouselEnabled = canActivateTwoTowersNonAUHeroCarousel && isTwoTowersNonAUHeroCarouselVariant

  const canActivateLerePersonalisedHomePageHeroCarousel = lereHeroCarouselEnabled &&
    !isSpoofed &&
    !isPostLoginExperimentSyncing &&
    interleavedTwoTowersOfferList.offerIds.length >= 9 &&
    currentRegionCode === 'AU'
  const { variation: lerePersonalisedHomePageHeroCarouselVariant } = useExperiment(
    Experiments.lerePersonalisedHomePageHeroCarouselV5,
    { canActivate: canActivateLerePersonalisedHomePageHeroCarousel },
  )

  // Determine carousel configuration based on experiment variant or fallback logic
  const twoTowersAUHeroCarouselEnabled = canActivateLerePersonalisedHomePageHeroCarousel && lerePersonalisedHomePageHeroCarouselVariant === 'on'

  /** END: Lere Hero Carousel */

  const currentSiteTakeover = useCurrentSiteTakeover()
  const isSocieteDoublePointsTakeover = currentSiteTakeover?.id === SOCIETE_DOUBLE_POINTS_TAKEOVER_ID
  const isTriplePointsTakeover = currentSiteTakeover?.id === triplePointsTakeoverId
  const isTasteOfGoldTakeover = !!useAppSelector(getTasteOfGoldPromoCampaign)

  const carouselOverrides = useMemo(() => {
    let heroCarouselOfferList: App.OfferList | undefined
    let heroCarouselTitle: React.ReactNode | undefined
    let heroCarouselSubtitle: React.ReactNode | undefined
    let tpfmCarouselOfferList: App.OfferList | undefined
    let tpfmCarouselTitle: React.ReactNode | undefined
    let excludedTpfmOfferIds: Array<string> | undefined
    let heroCarouselListEventListener = handleHeroListEvent
    let tpfmCarouselListEventListener = onTPFMNonHeroListEvent

    const topPicksForYouTitle = (
      <FormattedMessage
        id="ZUqFsqu-8vwI98PQt3zi"
        defaultMessage="Top picks for <i>you</i>"
        description="Hero carousel title with italic 'you' on the homepage"
        values={{
          i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
        }}
      />
    )
    const todaysTopExclusiveOffersTitle = (
      <FormattedMessage
        id="l3wbcUEHDJMUgG_szQyX"
        defaultMessage="Today’s top <i>exclusive</i> offers"
        description="Top picks for me carousel title with italic 'exclusive'"
        values={{
          i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
        }}
      />
    )

    // Force loading state for all carousels until ALL offerLists have finished fetching
    // Also wait for experiment post-login sync so we don't flash the static carousel for users who may fall into the on group
    if (isPostLoginExperimentSyncing || heroOffers.fetching || interleavedTwoTowersOfferList.fetching || userPageViewsLast7DaysFetching) {
      const placeholderOfferList: App.OfferList = { offerIds: [], fetching: true, key: 'placeholder-offer-list' }
      heroCarouselOfferList = placeholderOfferList
      tpfmCarouselOfferList = placeholderOfferList
    }

    if (twoTowersAUHeroCarouselEnabled) {
      heroCarouselOfferList = { ...interleavedTwoTowersOfferList, offerIds: unique([firstCuratedOffer, ...interleavedTwoTowersOfferList.offerIds]) }
      heroCarouselTitle = topPicksForYouTitle
      if (isSocieteDoublePointsTakeover) {
        heroCarouselTitle = <DoublePointTakeoverTitleContent />
        heroCarouselSubtitle = <DoublePointTakeoverSubtitleContent />
      } else if (isTriplePointsTakeover) {
        heroCarouselTitle = <TriplePointTakeoverHeroTitle />
        heroCarouselSubtitle = <TriplePointTakeoverCampaignSubtitle heroTakeover viewOffersTo={triplePointsRoute} />
      } else if (isTasteOfGoldTakeover) {
        heroCarouselTitle = <Group direction="horizontal" gap={16} verticalAlign="center">
          <CSSBreakpoint min="tablet"><TasteOfGoldHeroLogo /></CSSBreakpoint>
          <GoldText>
            {intl.formatMessage({
              id: 'Ytbx_91Z17iU5i_YWOsw',
              defaultMessage: 'Top picks for you',
              description: 'Hero carousel title for Taste of Gold takeover on homepage',
            })}
          </GoldText>
        </Group>
        heroCarouselSubtitle = <TasteOfGoldHeroSubtitleContent variant="subtitle-only" />
      }
      tpfmCarouselOfferList = { ...heroOffers, offerIds: without(heroOffers.offerIds, ...heroCarouselOfferList.offerIds) }
      tpfmCarouselTitle = todaysTopExclusiveOffersTitle
      heroCarouselListEventListener = handleTPFMHeroListEvent
      tpfmCarouselListEventListener = onCuratedNonHeroListEvent
    }
    else if (twoTowersNonAUHeroCarouselEnabled) {
      heroCarouselOfferList = { ...twoTowersOfferList, offerIds: unique([firstCuratedOffer, ...twoTowersOfferList.offerIds]) }
      heroCarouselTitle = topPicksForYouTitle
      if (isSocieteDoublePointsTakeover) {
        heroCarouselTitle = <DoublePointTakeoverTitleContent />
        heroCarouselSubtitle = <DoublePointTakeoverSubtitleContent />
      } else if (isTriplePointsTakeover) {
        heroCarouselTitle = <TriplePointTakeoverHeroTitle />
        heroCarouselSubtitle = <TriplePointTakeoverCampaignSubtitle heroTakeover viewOffersTo={triplePointsRoute} />
      } else if (isTasteOfGoldTakeover) {
        heroCarouselTitle = <Group direction="horizontal" gap={16} verticalAlign="center">
          <CSSBreakpoint min="tablet"><TasteOfGoldHeroLogo /></CSSBreakpoint>
          <GoldText>
            {intl.formatMessage({
              id: 'Ytbx_91Z17iU5i_YWOsw',
              defaultMessage: 'Top picks for you',
              description: 'Hero carousel title for Taste of Gold takeover on homepage',
            })}
          </GoldText>
        </Group>
        heroCarouselSubtitle = <TasteOfGoldHeroSubtitleContent variant="subtitle-only" />
      }
      tpfmCarouselOfferList = { ...heroOffers, offerIds: without(heroOffers.offerIds, ...heroCarouselOfferList.offerIds) }
      tpfmCarouselTitle = todaysTopExclusiveOffersTitle
      heroCarouselListEventListener = handleTPFMHeroListEvent
      tpfmCarouselListEventListener = onCuratedNonHeroListEvent
    }
    else {
      // No overrides. Ensure fetched offerIds by filters won't duplicate hero.
      // Note: 10% baseline group of lere personalised carousel fall here too
      excludedTpfmOfferIds = heroOffers.offerIds
      return {
        excludedTpfmOfferIds,
        heroCarouselOfferList,
        tpfmCarouselOfferList,
        heroCarouselListEventListener,
        tpfmCarouselListEventListener,
      }
    }

    return {
      heroCarouselOfferList,
      heroCarouselTitle,
      heroCarouselSubtitle,
      tpfmCarouselOfferList,
      tpfmCarouselTitle,
      excludedTpfmOfferIds,
      heroCarouselListEventListener,
      tpfmCarouselListEventListener,
    }
  }, [
    heroOffers,
    interleavedTwoTowersOfferList,
    userPageViewsLast7DaysFetching,
    isSocieteDoublePointsTakeover,
    isTriplePointsTakeover,
    isPostLoginExperimentSyncing,
    isTasteOfGoldTakeover,
    twoTowersAUHeroCarouselEnabled,
    twoTowersNonAUHeroCarouselEnabled,
    twoTowersOfferList,
    handleTPFMHeroListEvent,
    onTPFMNonHeroListEvent,
    onCuratedNonHeroListEvent,
    handleHeroListEvent,
    firstCuratedOffer,
    intl,
  ])

  /**
   * Individual useMemo hooks for each carousel section
   * Each carousel has its own memoization with specific dependencies
   */
  const trendingDestinationsCarousel = useMemo(() => (
    <OfferListEventsProvider onListEvent={onTrendingDestinationsListEvent}>
      <TrendingDestinations />
    </OfferListEventsProvider>
  ), [onTrendingDestinationsListEvent])

  const topPicksForMeCarousel = useMemo(() => {
    if (isAgentHubEnabled) {
      return null
    }
    return (
      <OfferListEventsProvider onListEvent={carouselOverrides.tpfmCarouselListEventListener}>
        <TopPicksForMeCarousel
          onHotelListEvent={onHotelListEvent}
          overrideTitle={carouselOverrides?.tpfmCarouselTitle}
          overrideOfferList={carouselOverrides?.tpfmCarouselOfferList}
          excludedIds={carouselOverrides?.excludedTpfmOfferIds}
        />
      </OfferListEventsProvider>
    )
  }, [carouselOverrides, onHotelListEvent])

  const onlyInAustraliaHeroCarousel = useMemo(() => {
    if (isAgentHubEnabled || !isOnlyInAustraliaHeroCarouselEnabled) {
      return null
    }
    return (
      <OfferListEventsProvider onListEvent={onHotelListEvent}>
        <OnlyInAustraliaHeroCarousel />
      </OfferListEventsProvider>
    )
  }, [isOnlyInAustraliaHeroCarouselEnabled, onHotelListEvent])

  const maldivesCharterCarousel = useMemo(() => {
    if (isAgentHubEnabled || !maldivesCharterCarouselEnabled || !!currentSiteTakeover) {
      return null
    }
    return (
      <OfferListEventsProvider onListEvent={onHotelListEvent}>
        <MaldivesCharterCarousel />
      </OfferListEventsProvider>
    )
  }, [onHotelListEvent, maldivesCharterCarouselEnabled, currentSiteTakeover])

  const offerEndingSoonCarousel = useMemo(() => {
    if (isAgentHubEnabled) {
      return null
    }
    return (
      <OfferListEventsProvider onListEvent={onEndingSoonListEvent}>
        <OfferEndingSoonCarousel />
      </OfferListEventsProvider>
    )
  }, [onEndingSoonListEvent])

  const homepageFlightCampaignsCarousel = useMemo(() => {
    if (campaignsOrDeals !== 'campaigns') {
      return null
    }
    return (
      <OfferListEventsProvider onListEvent={onFlightListEvent}>
        <InView rootMargin="100px 0px 0px 0px" triggerOnce>
          <HomepageFlightCampaigns />
        </InView>
      </OfferListEventsProvider>
    )
  }, [campaignsOrDeals, onFlightListEvent])

  const homepageFlightDealsCarousel = useMemo(() => {
    if (campaignsOrDeals !== 'deals') {
      return null
    }
    return (
      <OfferListEventsProvider onListEvent={onFlightListEvent}>
        <InView rootMargin="100px 0px 0px 0px" triggerOnce>
          <FlightDealsContainer>
            <HomepageFlightDeals />
          </FlightDealsContainer>
        </InView>
      </OfferListEventsProvider>
    )
  }, [campaignsOrDeals, onFlightListEvent])

  const societeStayUpgradeCarousel = useMemo(() => {
    if (!showSocieteStayUpgradeCarousel) {
      return null
    }
    return (
      <OfferListEventsProvider onListEvent={onHotelListEvent}>
        <SocieteStayUpgradeCarousel />
      </OfferListEventsProvider>
    )
  }, [showSocieteStayUpgradeCarousel, onHotelListEvent])

  const tourOfferCarousel = useMemo(() => (
    <OfferListEventsProvider onListEvent={onTourListEvent}>
      <TourOfferCarousel excludedIds={excludedTourIds} />
    </OfferListEventsProvider>
  ), [onTourListEvent, excludedTourIds])

  const supportTile = useMemo(() => {
    if (!showSupportContainer) {
      return null
    }
    return (
      <LayoutContainer>
        <SupportTile />
      </LayoutContainer>
    )
  }, [showSupportContainer])

  const cruiseOfferCarousel = useMemo(() => {
    if (!isStandaloneCruiseEnabled) {
      return null
    }
    return (
      <OfferListEventsProvider onListEvent={onCruiseListEvent}>
        <CruiseOfferCarousel />
        <CruiseRoyalCaribbeanCampaignCarousel />
      </OfferListEventsProvider>
    )
  }, [isStandaloneCruiseEnabled, onCruiseListEvent])

  const trustIndicatorSection = useMemo(() => {
    if (isLoggedIn) {
      return null
    }
    return (
      <>
        {campaignsOrDeals !== 'none' && <PaddedLayoutContainer />}
        <LayoutContainer>
          <VerticalSpacer gap={40}>
            <Divider kind="primary" />
            <TrustIndicatorSection />
            <Divider kind="primary" />
          </VerticalSpacer>
        </LayoutContainer>
      </>
    )
  }, [isLoggedIn, campaignsOrDeals])

  const experienceLuxPlusCarousel = useMemo(() => {
    if (!isLuxPlusEnabled || !canRedeemLuxPlusBenefits) {
      return null
    }
    return (
      <OfferListEventsProvider onListEvent={onExperienceListEvent}>
        <ExperienceLuxPlusCarousel />
      </OfferListEventsProvider>
    )
  }, [isLuxPlusEnabled, canRedeemLuxPlusBenefits, onExperienceListEvent])

  const smartExperienceOfferCarousel = useMemo(() => {
    if (isLuxPlusEnabled && canRedeemLuxPlusBenefits) {
      return null
    }
    return (
      <OfferListEventsProvider onListEvent={onExperienceListEvent}>
        <SmartExperienceOfferCarousel />
      </OfferListEventsProvider>
    )
  }, [isLuxPlusEnabled, canRedeemLuxPlusBenefits, onExperienceListEvent])

  const marketingCarousel = useMemo(() => <MarketingCarousel />, [])

  const luxPlusOfferCarousel = useMemo(() => {
    if (!isLuxPlusEnabled) {
      return null
    }
    return (
      <OfferListEventsProvider onListEvent={onLuxPlusListEvent}>
        <LuxPlusOfferCarousel />
      </OfferListEventsProvider>
    )
  }, [isLuxPlusEnabled, onLuxPlusListEvent])

  const hotelOfferCarousel = useMemo(() => (
    <OfferListEventsProvider onListEvent={onBestValueHotelListEvent}>
      <HotelOfferCarousel sortBy="value" streamList />
    </OfferListEventsProvider>
  ), [onBestValueHotelListEvent])

  const ultraLuxOfferCarousel = useMemo(() => {
    if (!config.ULTRALUX_ENABLED) {
      return null
    }
    return (
      <OfferListEventsProvider onListEvent={onUltraLuxListEvent}>
        <UltraLuxOfferCarousel />
      </OfferListEventsProvider>
    )
  }, [onUltraLuxListEvent])

  const isMerchandiseEnabled = useAppSelector(getIsMerchandiseEnabled)
  const locale = useAppSelector((state) => state.geo.locale)

  const merchandisePointsCarousel = useMemo(() => {
    if (currentRegionCode !== 'AU' || !isMerchandiseEnabled || !isEnglishLocale(locale)) {
      return null
    }
    return <MerchandisePointsCarousel trackingPageName="Homepage" />
  }, [currentRegionCode, isMerchandiseEnabled, locale])

  const componentBannerGroup = useMemo(() => (
    <>
      <ComponentBannerGroup slug="homepage-banner-position-one" />
      {bannersBelowHero && bannersBelowHero?.length > 0 && (
        <LeaderboardLayoutContainer>
          <LayoutContainer>
            <Leaderboard banners={bannersBelowHero || EMPTY_BANNERS} />
          </LayoutContainer>
        </LeaderboardLayoutContainer>
      )}
    </>
  ), [bannersBelowHero])

  const upsellSection = useMemo(() => {
    if (!showUpsellSection) {
      return null
    }
    return (
      <HomepageSection>
        <Group direction="vertical" gap={32}>
          <OfferDetailUpsellSection order={upsellOrder} />
          <CompactBookingDetailsUpsellSection order={upsellOrder} />
        </Group>
      </HomepageSection>
    )
  }, [showUpsellSection, upsellOrder])

  const frontPageUSP = useMemo(() => {
    if (!showFrontPageUSP) {
      return null
    }
    return (
      <HomepageSection>
        <VerticalSpacer gap={40}>
          <FrontPageUSPComponent />
          <Divider kind="primary" />
        </VerticalSpacer>
      </HomepageSection>
    )
  }, [showFrontPageUSP])

  const carHireBundledUpcomingOrder = useMemo(() => {
    if (!showBundledCarHireCarousel) {
      return null
    }
    return (
      <CarHireCarouselSection>
        <CarHireBundledUpcomingOrderCondensed order={upsellOrder} />
      </CarHireCarouselSection>
    )
  }, [showBundledCarHireCarousel, upsellOrder])

  const valuePropositionBanner = useMemo(() => <ValuePropositionBanner />, [])
  const blogCarousel = useMemo(() => <BlogCarousel />, [])
  const browseOfferTypes = useMemo(() => <BrowseOfferTypes />, [])

  /**
   * Direct mapping of carousel section identifiers to their useMemo hooks
   */
  const carouselSectionMap = useMemo(() => ({
    componentBannerGroup,
    upsellSection,
    frontPageUSP,
    carHireBundledUpcomingOrder,
    trendingDestinations: trendingDestinationsCarousel,
    onlyInAustraliaHeroCarousel,
    maldivesCharterCarousel,
    offerEndingSoonCarousel,
    trip: null,
    topPicksForMeCarousel,
    homepageFlightCampaigns: homepageFlightCampaignsCarousel,
    homepageFlightDeals: homepageFlightDealsCarousel,
    societeStayUpgradeCarousel,
    tourOfferCarousel,
    supportTile,
    cruiseOfferCarousel,
    trustIndicatorSection,
    experienceLuxPlusCarousel,
    smartExperienceOfferCarousel,
    marketingCarousel,
    luxPlusOfferCarousel,
    hotelOfferCarousel,
    ultraLuxOfferCarousel,
    merchandisePointsCarousel,
    valuePropositionBanner,
    blogCarousel,
    browseOfferTypes,
  }), [
    componentBannerGroup,
    upsellSection,
    frontPageUSP,
    carHireBundledUpcomingOrder,
    trendingDestinationsCarousel,
    onlyInAustraliaHeroCarousel,
    maldivesCharterCarousel,
    offerEndingSoonCarousel,
    topPicksForMeCarousel,
    homepageFlightCampaignsCarousel,
    homepageFlightDealsCarousel,
    societeStayUpgradeCarousel,
    tourOfferCarousel,
    supportTile,
    cruiseOfferCarousel,
    trustIndicatorSection,
    experienceLuxPlusCarousel,
    smartExperienceOfferCarousel,
    marketingCarousel,
    luxPlusOfferCarousel,
    hotelOfferCarousel,
    ultraLuxOfferCarousel,
    merchandisePointsCarousel,
    valuePropositionBanner,
    blogCarousel,
    browseOfferTypes,
  ])

  return (
    <AnalyticsPageContext.Provider value={AnalyticsPage.homepage}>
      <HomepageCarouselContext.Provider value>
        <Pane>
          <ErrorBoundary fallback={null}>
            <PromptCardSelector tripReengagement carHire />
          </ErrorBoundary>
          <ErrorBoundary fallback={null}>
            <LeLivePrompt />
          </ErrorBoundary>
          <OfferListEventsProvider onListEvent={carouselOverrides.heroCarouselListEventListener}>
            {bannersAboveHero && bannersAboveHero?.length > 0 && (
              <LeaderboardLayoutContainer paletteType="inverse">
                <LayoutContainer>
                  <Leaderboard banners={bannersAboveHero || EMPTY_BANNERS} />
                </LayoutContainer>
              </LeaderboardLayoutContainer>
            )}
            <HeroOfferCarouselSection
              overrideTitle={carouselOverrides?.heroCarouselTitle}
              overrideSubtitle={carouselOverrides?.heroCarouselSubtitle}
              overrideOfferList={carouselOverrides?.heroCarouselOfferList}
              lereHeroCarouselEnabled={lereHeroCarouselEnabled}
            />
          </OfferListEventsProvider>
          {!hideHomepageSearch && (
            <GlobalSearchTrackingProvider onEvent={onSearchEvent}>
              <Search />
            </GlobalSearchTrackingProvider>
          )}
          {shouldShowPostTripReviewNudge && (
            <ErrorBoundary fallback={null}>
              <PostTripReviewNudge />
            </ErrorBoundary>
          )}
          <ErrorBoundary fallback={null}>
            <RecentSearches />
          </ErrorBoundary>
          {isLoggedIn && config.ENABLE_MY_VOUCHERS_HOMEPAGE_CAROUSEL && <MyVouchersCarousel />}
          {carouselSectionOrder.map(identifier => {
            const carousel = carouselSectionMap[identifier]
            return carousel ? <React.Fragment key={identifier}>{carousel}</React.Fragment> : null
          })}
        </Pane>
      </HomepageCarouselContext.Provider>
    </AnalyticsPageContext.Provider>
  )
}

function mapStateToProps(state: App.State): MappedProps {
  return {
    banners: state.content.banners.home,
    isLoggedIn: selectLoggedIn(state),
    isStandaloneCruiseEnabled: isStandaloneCruiseEnabled(state),
    isLuxPlusEnabled: getIsLuxPlusEnabled(state),
    canRedeemLuxPlusBenefits: getCanRedeemLuxPlusBenefits(state),
    carHireBundledCarouselEnabled: isCarHireBundledHomepageCarouselEnabled(state),
    maldivesCharterCarouselEnabled: isMaldivesCharterCarouselEnabled(state),
  }
}

export default connect(mapStateToProps)(HomePage)
